import { createContext } from "react";
import { createContextualCan } from "@casl/react";

import { Ability, AbilityClass } from "@casl/ability";

import useCan from "./useCan";

type Actions = "see";
export type Subjects =
    | "ADMIN_LICENCING"
    | "DRAX_CRM"
    | "LD_BASIC"
    | "LD_FREE"
    | "LD_LIMITED"
    | "LD_SEARCH_COMPANY"
    | "LD_SEARCH_SAVED_GRP"
    | "LD_SEARCH_SAVED_OWN"
    | "LD_PROJECT_CREATE"
    | "LD_PROJECT_EDIT"
    | "LD_PROJECT_SEARCH"
    | "LD_PROJECT_VIEW"
    | "LD_SEARCH_COMPANY_ADVANCED"
    | "LD_TEAM"
    | "LD_TEAM_LINKEDIN"
    | "LD_TALENT"
    | "LD_BRIDGE"
    | "LD_INSIGHTS_BALANCE"
    | "LD_INSIGHTS_BALANCE_DUPLICATE"
    | "LD_INSIGHTS_BALANCE_REPORT"
    | "LD_INSIGHTS_BEHAVIOURAL"
    | "LD_INSIGHTS_BEHAVIOURAL_BREAK_DOWN"
    | "LD_INSIGHTS_COMPARISON"
    | "LD_INSIGHTS_DOMAIN"
    | "LD_INSIGHTS_FUNCTIONAL"
    | "LD_INSIGHTS_INDIVIDUAL_POS"
    | "LD_INSIGHTS_LSPM"
    | "LD_INSIGHTS_POTENTIAL"
    | "LD_INSIGHTS_SITUATIONAL"
    | "LD_SEARCH_AI"
    | "PACE"
    | "PACE_ADMIN_PORTAL"
    | "PACE_ASSESSMENT"
    | "PACE_REPORT_RUN_GRP"
    | "PACE_REPORT_RUN_OWN"
    // NEW PERMISSIONS
    | "SAAS_LD_PROJECT"
    | "SAAS_LD_PROJECT_BRIEF_CREATE"
    | "SAAS_LD_PROJECT_CREATE_GRP"
    | "SAAS_LD_PROJECT_INSIGHT_CHART_BEHAVIOURAL_BREAKDOWN"
    | "SAAS_LD_PROJECT_INSIGHT_CHART_BEHAVIOURAL_COMPLEMENTARITY_PROJECTED"
    | "SAAS_LD_PROJECT_INSIGHT_CHART_BEHAVIOURAL_COMPLEMENTARITY_REAL_PACE"
    | "SAAS_LD_PROJECT_INSIGHT_CHART_DOMAIN_BREAKDOWN"
    | "SAAS_LD_PROJECT_INSIGHT_CHART_EXECUTIVE_COMPARABLE"
    | "SAAS_LD_PROJECT_INSIGHT_CHART_FUNCTIONAL_BALANCE"
    | "SAAS_LD_PROJECT_INSIGHT_CHART_FUNCTIONAL_BREAKDOWN"
    | "SAAS_LD_PROJECT_INSIGHT_CHART_LEADERSHIP_BRIDGE"
    | "SAAS_LD_PROJECT_INSIGHT_CHART_POTENTIAL"
    | "SAAS_LD_PROJECT_INSIGHT_CHART_SITUATIONAL_BREAKDOWN"
    | "SAAS_LD_PROJECT_INSIGHT_CREATE"
    | "SAAS_LD_PROJECT_INSIGHT_DOMAIN_EDIT"
    | "SAAS_LD_PROJECT_INSIGHT_DOMAIN_PEERS_BESPOKE_1"
    | "SAAS_LD_PROJECT_INSIGHT_DOMAIN_PEERS_BESPOKE_6"
    | "SAAS_LD_PROJECT_INSIGHT_DOMAIN_PEERS_SEARCH_ADV"
    | "SAAS_LD_PROJECT_INSIGHT_TEAM_4"
    | "SAAS_LD_PROJECT_INSIGHT_TEAM_EDIT"
    | "SAAS_LD_PROJECT_INSIGHT_VCP_EDIT"
    | "SAAS_LD_PROJECT_INSIGHT_VCP_FIXED"
    | "SAAS_LD_PROJECT_SEARCH_AI_CREATE"
    | "SAAS_LD_PROJECT_SEARCH_AI_COMPANY"
    | "SAAS_LD_PROJECT_SEARCH_AI_PEOPLE"
    | "SAAS_LD_PROJECT_SEARCH_AI_REFINE"
    | "SAAS_LD_PROJECT_INVITE_USERS"
    | "SAAS_LD_PROJECT_SEARCH_GRP"
    | "SAAS_LD_PROJECT_VIEW_GRP"
    | "SAAS_PACE"
    | "SAAS_PACE_ASSESSMENT"
    | "SAAS_PACE_DOWNLOAD_PDF"
    | "SAAS_PACE_DASHBOARD_BEHAVIOURAL_COMPARISON"
    | "SAAS_PACE_REPORT"
    | "SAAS_PROFILE"
    | "SAAS_PROFILE_PACE_CLAIM"
    | "SAAS_PROFILE_VIEW_DASHBOARD"
    | "SAAS_PROFILE_VIEW_INSIGHTS"
    | "SAAS_PROFILE_WORK_HISTORY_EDIT"
    | "SAAS_PACE_PORTAL_INVITE_USERS"
    | "SYS_ADMIN"
    | "SYS_ADMIN_2"
    | "SYS_ADMIN_DIAGNOSTICS"
    | "SYS_ADMIN_GROUP_ADD"
    | "SYS_ADMIN_GROUP_EDIT"
    | "SYS_ADMIN_INVITE_USERS"
    | "SYS_ADMIN_ORG_ADD"
    | "SYS_ADMIN_ORG_CHILD_ADD"
    | "SYS_ADMIN_ORG_CHILD_EDIT"
    | "SYS_ADMIN_ORG_EDIT"
    | "SYS_ADMIN_PACE_TRANSFER"
    | "SYS_ADMIN_SCOPE_GRPS_OWN"
    | "SYS_ADMIN_SCOPE_GRPS_ALL"
    | "SYS_ADMIN_USER_ADD"
    | "SYS_ADMIN_USER_EDIT"
    | "SYS_ADMIN_USER_GROUPS_EDIT"
    | "SYS_ADMIN_USER_GROUP_ROLES_EDIT"
    | "SYS_ADMIN_USER_ROLES_EDIT"
    | "SYS_ADMIN_VIEW_CLIENT_HUB"
    | "SYS_ADMIN_VIEW_PACE_PORTAL"
    | "SYS_ADMIN_VIEW_USER_MANAGEMENT"
    | "all";

/**
 * Initial notes about permissions
 *
 *
 * LD -> The Leadership Dynamics Tab at the top - Everything on the left (NO "Start Talent Search" button on Summary Modal).
 * LD_TALENT_LINK -> Everything in LD (LD should be there too) and the "Start Talent Search" button in the Summary Modal.
 * PACE -> The PACE Tab at the top - The "Take Assessment" and "View Report" buttons on the left.
 * PACE_ASSESSMENT -> This is the same as PACE.
 * PACE_REPORT_RUN_OWN ->  means the View Report button should be there
 * PACE_ADMIN_PORTAL -> The "Portal Admin" section should be available too (PACE should be present too already)
 * PACE_REPORT_RUN_GRP -> This should only ever be present when PACE_ADMIN_PORTAL is - I think (currently)
 *
 * Roles
 * LD
 * PACE_BASIC -> PACE, PACE_ASSESSMENT, PACE_REPORT_RUN_OWN
 * PACE_PORTAL -> PACE, PACE_ASSESSMENT, PACE_REPORT_RUN_OWN, PACE_ADMIN_PORTAL, PACE_REPORT_GRP
 * LD_BASIC -> LD
 * LD_TALENT -> LD, LD_TALENT_LINK
 */

export type TAppAbility = Ability<[Actions, Subjects]>;
export const AppAbility = Ability as AbilityClass<TAppAbility>;

const AbilityContext = createContext<TAppAbility>(undefined!);
AbilityContext.displayName = "AbilityContext";

const AbilityProvider = ({ children }: any): JSX.Element => {
    const can = useCan();

    // return <AbilityContext.Provider value={ability.current}>{children}</AbilityContext.Provider>;
    return <AbilityContext.Provider value={can}>{children}</AbilityContext.Provider>;
};

export default createContextualCan(AbilityContext.Consumer);

export { AbilityProvider, AbilityContext };
